// src/pages/CustomerManagement/InvoicesTable.tsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import styles from "./InvoicesTable.module.scss";

interface Invoice {
  id: number;
  reservation_code: string;
  invoice: string;
  user_id: number | null;
  full_name: string;
  email: string;
  app_time: string;
  appointment: boolean;
}

interface InvoicesTableProps {
  onAddInvoice: () => void;
}

const PAGE_SIZE = 25;

const InvoicesTable: React.FC<InvoicesTableProps> = ({ onAddInvoice }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // 实现搜索输入的防抖
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // 当搜索改变时，重置到第一页
    }, 500); // 500ms防抖

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const offset = page * PAGE_SIZE;
        let url = `/invoices?offset=${offset}&limit=${PAGE_SIZE}`;
        if (debouncedSearch.trim() !== "") {
          const encodedSearch = encodeURIComponent(debouncedSearch.trim());
          url += `&search=${encodedSearch}`;
        }
        const res = await api.get(url);
        setInvoices(res.data.invoices);
        setTotalCount(res.data.total);
      } catch (error) {
        console.error("Failed to load invoices:", error);
      }
    };

    fetchInvoices();
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handlePrevious = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className={styles.invoicesTableContainer}>
      {/* Header: Total Invoices and Add Invoice Button */}
      <div className={styles.header}>
        <div className={styles.totalInvoices}>
          <strong>{t("customerManagement.totalInvoices")}:</strong> {totalCount}
        </div>
        <button onClick={onAddInvoice} className={styles.addInvoiceButton}>
          {t("customerManagement.addInvoice")}
        </button>
      </div>

      {/* 搜索框 */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder={t("customerManagement.searchPlaceholderInvoice")}
          value={searchQuery}
          onChange={handleSearchChange}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.invoiceTable}>
          <thead>
            <tr>
              <th>{t("customerManagement.reservationCode")}</th>
              <th>{t("customerManagement.invoice")}</th>
              <th>{t("customerManagement.userId")}</th>
              <th>{t("customerManagement.appTimeLabel")}</th>
              <th>{t("customerManagement.appointment")}</th>
              <th>{t("customerManagement.fullName")}</th>
              <th>{t("customerManagement.email")}</th>
              <th>{t("customerManagement.edit")}</th> {/* 新增编辑列 */}
            </tr>
          </thead>
          <tbody>
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <tr key={invoice.invoice}>
                  <td>{invoice.reservation_code}</td>
                  <td>{invoice.invoice}</td>
                  <td>{invoice.user_id !== null ? invoice.user_id : "N/A"}</td>
                  <td>{invoice.app_time}</td>
                  <td>
                    {invoice.appointment ? t("general.yes") : t("general.no")}
                  </td>
                  <td>{invoice.full_name}</td>
                  <td>{invoice.email}</td>
                  <td>
                    <button
                      onClick={() =>
                        navigate(
                          `/customer_management/edit/invoice/${invoice.id}`
                        )
                      }
                      className={styles.editButton}
                    >
                      {t("customerManagement.edit")}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className={styles.noInvoicesFound}>
                  {t("customerManagement.noInvoicesFound")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      <div className={styles.pagination}>
        <button onClick={handlePrevious} disabled={page === 0}>
          {t("customerManagement.prev")}
        </button>
        <span>
          {t("customerManagement.page")} {page + 1} {t("customerManagement.of")}{" "}
          {totalPages}
        </span>
        <button onClick={handleNext} disabled={page >= totalPages - 1}>
          {t("customerManagement.next")}
        </button>
      </div>
    </div>
  );
};

export default InvoicesTable;
